// ProtectedRoute.js - Route protection component
import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';

const AuthSpinner = () => {
  // Watchdog: if auth initialization hasn't resolved after 15s, offer
  // recovery instead of spinning forever. AuthContext's own failsafe fires
  // at 12s, so this only renders when something is truly wedged.
  const [timedOut, setTimedOut] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setTimedOut(true), 15000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        gap: 2,
        p: 2,
        textAlign: 'center',
      }}
    >
      <CircularProgress size={60} />
      {!timedOut ? (
        <Typography variant="body1" color="text.secondary">
          Loading...
        </Typography>
      ) : (
        <>
          <Typography variant="body1" color="text.secondary">
            This is taking longer than expected. Check your connection.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="contained" onClick={() => window.location.reload()}>
              Reload
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                try {
                  localStorage.removeItem('sb-auth-token');
                } catch { /* ignore */ }
                window.location.assign('/login');
              }}
            >
              Sign in again
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

const ProtectedRoute = ({ children }) => {
  const { user, loading, requiresPasswordChange } = useAuth();
  const location = useLocation();

  // Auth is still initializing — show spinner (with 15s recovery watchdog)
  if (loading) {
    return <AuthSpinner />;
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Force password change: redirect to profile page (unless already there)
  if (requiresPasswordChange && location.pathname !== '/profile') {
    return <Navigate to="/profile" replace />;
  }

  // User is authenticated and profile is ready
  return children;
};

export default ProtectedRoute;
